import { error } from '../../../core/js/utils/logger';

import queryString from 'query-string';

import { ALLOWED_PRODUCT_QUERY_PARAMS } from '../enums/productEnums';

import {
    L10N_PRODUCT_AVAIL_PREORDER,
    L10N_PRODUCT_AVAIL_BACKORDER,
    L10N_PRODUCT_AVAIL_LOW_STOCK,
    L10N_PRODUCT_AVAIL_OUT_OF_STOCK,
    L10N_PRODUCT_AVAIL_DISCONTINUED,
    L10N_PRODUCT_AVAIL_DROPSHIP,
    L10N_PRODUCT_AVAIL_MADE_TO_ORDER,
    L10N_PRODUCT_AVAIL_IN_STOCK_MADE_TO_ORDER,
    L10N_PRODUCT_AVAIL_ONE_LEFT,
    L10N_PRODUCT_AVAIL_TWO_LEFT,
    ERR_SIZE_NOT_IN_COLOR,
    ERR_SIZE_TYPE_INVALID,
    ERR_SIZE_COLOR_OOS,
    ERR_SIZE_OOS,
    ERR_SIZE_FIT_OOS,
    ERR_SELECT_SIZE,
    ERR_SIZE_NOT_IN_FIT
} from '../../../core/js/enums/localizationEnums';

import { setLocalStorageItemWithFallback } from '@core/js/utils/cacheUtils';

import { getQueryParamsUrl } from '../../../core/js/utils/routerUtils';

import {
    COOKIE_NAME_INVENTORY_POOL,
    COOKIE_NAME_SITE_ID,
    SKU_AVAILABILITY_AVAILABLE,
    SKU_AVAILABILITY_OUT_OF_STOCK,
    SKU_AVAILABILITY_DISCONTINUED,
    PURCHASABLE_SKU_STATUS_CODES
} from '../../../core/js/constants';

import Cookies from '../../../core/js/services/cookies';

import { isQuickshop } from './quickshopUtils';

import { useGroundhog } from '../../../core/js/utils/abTests';

/**
 * @param action Action being passed around
 * @returns boolean, true if this action is for a family product
 */
export function actionIsForFamilyProduct(action) {
    if (action) {
        if (action.meta && action.meta.component) {
            const component = action.meta.component;

            if (component.forFamilyProduct) {
                return true;
            }
        }
    }

    return false;
}

/**
 * @param state Redux state
 * @param action Current action being performed.  This action contains meta data about family product indices (if any)
 * @returns either the main pdp product or a family product
 */
export function pdpSelector(state, action) {
    if (actionIsForFamilyProduct(action)) {
        const familyProductIndex = action.meta.component.multipleIndex;

        return state.product.familyProducts[familyProductIndex];
    }

    return state.product;
}

/**
 * @param state Redux state
 * @param familyProductIndex index of the family product within the current product
 * @returns the inner family product at the inputted index
 */
export function familyProductSelector(state, familyProductIndex) {
    return state.product.familyProducts[familyProductIndex];
}

/**
 * @param state Redux state
 * @returns The quickshop product
 */
export function quickshopProductSelector(state) {
    return state.quickshop.product;
}

// TODO UW-3252 refactor with quickshop refactor
// manually checking every action to verify it's a quickshop
// action is both developer intensive and untenable for
// non-product actions i.e. core actions
// need a better way to handle this
export function getProductForAction(state, action) {
    if (isQuickshop(state)) {
        return quickshopProductSelector(state);
    }

    return pdpSelector(state, action);
}

/**
 * @param state Redux state
 * @param familyProductIndex index of the family product within the current product
 * @returns the inner family product at the inputted index
 */
export function getActiveFamilyProduct(state, familyProductIndex) {
    if (isQuickshop(state)) {
        error("Trying to read family product from Quickshop. Not implemented");
    }

    return familyProductSelector(state, familyProductIndex);
}

/**
 * Returns either the base pdp product (state.product) or
 *  the quickshop product (state.quickshop.product).
 * @param state Redux state
 * @param familyProductIndex optional, if passed, pulls the nth family product
 * @return pdp or quickshop product
 */
export function getActiveProduct(state, familyProductIndex) {
    if (!_.isUndefined(familyProductIndex)) {
        return getActiveFamilyProduct(state, familyProductIndex);
    }

    if (isQuickshop(state)) {
        return quickshopProductSelector(state);
    }

    return state.product;
}

/**
 * @param state Redux state
 * @param familyProductIndex optional, if passed, pulls the nth family product
 * @returns Gets a lodash/pydash type dictionary path lookup: 'product' or 'quickshop.product'
 */
export function getActiveProductSelector(state, familyProductIndex) {
    if (!_.isUndefined(familyProductIndex)) {
        return `product.familyProducts.${familyProductIndex}`;
    }

    if (isQuickshop(state)) {
        return 'quickshop.product';
    }

    return 'product';
}

export function getTargetedProduct(state, component) {
    const forQuickshop = isQuickshop(state);
    const fakeAction = {
        meta: {
            forQuickshop: forQuickshop,
            component: component
        }
    };

    return getProductForAction(state, fakeAction);
}

/**
 * Get Total stock based on passed in sku
 * @param {object} skuObj current sku to get stock from
 * @returns {number} total sku stock
 */
export function getTotalStock(skuObj) {
    if (!skuObj) {
        return 0;
    }

    const stockLevel = skuObj.stockLevel || 0;
    const backorder = skuObj.backorder || 0;
    const preorder = skuObj.preorder || 0;

    return stockLevel + backorder + preorder;
}

/**
 * Get direct inventory stock based on passed in sku
 * @param {object} skuObj current sku to get stock from
 * @returns {number}
 */
export function getDirectInventoryStock(skuObj) {
    if (!skuObj) {
        return 0;
    }

    const stockLevel = skuObj.stockLevel || 0;

    return stockLevel;
}

/**
 * Get backorder inventory stock based on passed in sku
 * @param {object} skuObj current sku to get stock from
 * @returns {number}
 */
export function getBackorderInventoryStock(skuObj) {
    if (!skuObj) {
        return 0;
    }

    const stockLevel = skuObj.backorder || 0;

    return stockLevel;
}

/**
 * Get preorder inventory stock based on passed in sku
 * @param {object} skuObj current sku to get stock from
 * @returns {number}
 */
export function getPreorderInventoryStock(skuObj) {
    if (!skuObj) {
        return 0;
    }

    const stockLevel = skuObj.preorder || 0;

    return stockLevel;
}

// Only currently used in PDP's productInitialSaga
// If this is used in anything that quickshop could touch,
// this will need to be changed to use the getTargetedProduct method
export function getSelectedColorCode(state) {
    if (state &&
        state.product &&
        state.product.productControls) {
        return state.product.productControls.colorCode;
    }

    return undefined;
}

export function getInventoryPool() {
    return Cookies.getCookieByName(COOKIE_NAME_INVENTORY_POOL);
}

export function hasPreorderSkus(state) {
    const product = getTargetedProduct(state);
    return _.get(product, 'product.preorderFlag', false);
}

export function isProductMadeToOrder(state) {
    const product = getTargetedProduct(state);
    return _.get(product, 'product.isMadeToOrder', false);
}

/**
 * Gets the size ID that matches the name passed in
 * @param {object} selectedSecondarySliceItem selected secondary slice item
 * @param {string} name  size in payload or store
 * @returns {string|null}
 */
export function getSizeIdFromName(selectedSecondarySliceItem, name) {
    if (selectedSecondarySliceItem) {
        const sizes = selectedSecondarySliceItem.includedSizes;

        // loop through sizes in matching size typeCode

        for (let j = 0; j < sizes.length; j++) {
            // if name of size in size typeCode matches passed in name
            if (sizes[j].displayName === name) {
                return sizes[j].id;
            }
        }
    }

    return null;
}

/**
 * Gets the sku object that matches the selected sizeId,
 * ignoring sizeType (fit) if flag is provided.
 * @param {array} selectedPrimarySliceItem Selected primary slice item
 * @param {array} selectedSecondarySliceItem Selected secondary slice item
 * @param {number} selSizeTypeCode sizeTypeCode in payload or store
 * @param {string} selSizeId size in payload or store
 * @param {boolean} ignoreSizeType look for any matching size
 * @param {boolean} backInStockEnabled
 * @returns {object|null}
 */
 /* eslint-disable complexity */
export function getSkuObjBySizeId(selectedPrimarySliceItem, selectedSecondarySliceItem, selSizeTypeCode, selSizeId, ignoreSizeType = false, backInStockEnabled = false) {
    for (let i = 0; i < selectedPrimarySliceItem.includedSkus.length; i += 1) {
        const includedSku = selectedPrimarySliceItem.includedSkus[i];
        // if we want to ignore the size type,
        // we need to grab the sizeId since it will be different
        const includedSkuSizeId = ignoreSizeType ? getSizeIdFromName(selectedSecondarySliceItem, includedSku.size) : includedSku.sizeId;
        const totalStock = getTotalStock(includedSku);

        if (includedSkuSizeId === selSizeId && (ignoreSizeType || includedSku.sizeTypeCode === selSizeTypeCode)) {
            if (backInStockEnabled || totalStock > 0) {
                return includedSku;
            }
        }
    }

    return null;
}

/**
 * Gets the sku object that matches the currently selected
 * color and size combination. If it doesn't exist, it returns null.
 * @param {array} selectedPrimarySliceItem Selected primary slice item
 * @param {array} selectedSecondarySliceItem Selected secondary slice item
 * @param {string} selColorCode colorCode in payload or store
 * @param {number} selSizeTypeCode sizeTypeCode in payload or store
 * @param {string} selSizeId size in payload or store
 * @param {boolean} backInStockEnabled
 * @returns {object|null}
 */

 /* eslint-disable complexity */
export function getSkuObj(selectedPrimarySliceItem, selectedSecondarySliceItem, selColorCode, selSizeTypeCode, selSizeId, backInStockEnabled = false) {
    // TODO: update this function to use new pdp model
    if (selectedPrimarySliceItem && selectedSecondarySliceItem) {
        if (selectedSecondarySliceItem.includedSizes.length === 1 &&
            selectedSecondarySliceItem.includedSizes[0].colorCodes[selColorCode]) {
            const firstSku = selectedPrimarySliceItem.includedSkus[0];

            if (backInStockEnabled || getTotalStock(firstSku) > 0) {
                // Return first includedSku if there is only 1 size available for this color, if it has stock
                return firstSku;
            }
        }

        // Otherwise loop through all includedSkus to find the matching size
        let skuObj = getSkuObjBySizeId(selectedPrimarySliceItem, selectedSecondarySliceItem, selSizeTypeCode, selSizeId, false, backInStockEnabled);

        // If BIS and we still have no skuObj, check for this size ignoring sizeType
        if (backInStockEnabled && !skuObj) {
            skuObj = getSkuObjBySizeId(selectedPrimarySliceItem, selectedSecondarySliceItem, selSizeTypeCode, selSizeId, true, backInStockEnabled);
        }

        return skuObj;
    }

    return null;
}

/**
 * Get price of selected SKU (re-implementation of ProductModel.get_selected_price)
 * @param {object} skuObj Current sku
 * @param {object} skuInfo Sku information
 * @returns {number} Price of selected sku
 */
export function getSelectedPrice(skuObj, skuInfo) {
    if (!skuObj) {
        return null;
    }
    if (skuInfo.hasMarkdown) {
        if (skuObj && typeof skuObj.salePrice !== 'undefined') {
            return skuObj.salePrice;
        }
        return skuInfo.salePriceLow;
    }
    if (skuObj && typeof skuObj.listPrice !== 'undefined') {
        return skuObj.listPrice;
    }
    return skuInfo.listPriceHigh;
}

/**
 * Get the availability date for a project
 * @param  {function} state  Redux state function
 * @param  {object} skuObj      Current sku
 * @return {number|null}        Availability Date or null
 */
export function getAvailabilityDate(state, skuObj) {
    let availabilityDate = null;

    if (skuObj.availabilityDate) {
        const backorderPaddingDays = _.get(state, 'core.siteConfig.sites[0].backorderPaddingDays', 0);
        const d = new Date(skuObj.availabilityDate * 1000);

        d.setDate(d.getDate() + backorderPaddingDays);
        availabilityDate = d / 1000;
    }

    return availabilityDate;
}

/**
 * Check if a selected sku is available for preorder
 * @param  {object} skuObj      Current sku
 * @param  {function} state  Redux state function
 * @returns {true|false} selected SKU has preorder
 */
export function isSelectedSkuPreorder(skuObj, state) {
    return getDirectInventoryStock(skuObj) === 0 &&
        hasPreorderSkus(state) &&
        getBackorderInventoryStock(skuObj) > 0 &&
        getPreorderInventoryStock(skuObj) === 0;
}

/**
 * Get availability message for a sku that is available for preorder
 * @param  {string} availabilityDate      availability date for sku
 * @return {object}        Availability Message
 */
export function getPreorderAvailabilityMessage(availabilityDate) {
    return {
        slug: L10N_PRODUCT_AVAIL_PREORDER,
        date: availabilityDate,
        params: [availabilityDate]
    };
}

/**
 * Get availability message for a sku that is available for backorder
 * @param  {string} availabilityDate      availability date for sku
 * @return {object}        Availability Message
 */
export function getBackorderAvailabilityMessage(availabilityDate) {
    return {
        slug: L10N_PRODUCT_AVAIL_BACKORDER,
        date: availabilityDate,
        params: [availabilityDate]
    };
}

/**
 * Get availability message for a sku that has low inventory
 * @param  {string} availabilityDate      availability date for sku
 * @return {object}        Availability Message
 */
export function getLowInventoryAvailabilityMessage(availabilityDate) {
    return {
        slug: L10N_PRODUCT_AVAIL_LOW_STOCK
    };
}

/**
 * Get availability message for a sku that has 1 unit available
 * @param  {string} availabilityDate      availability date for sku
 * @return {object}        Availability Message
 */
export function getOneAvailableMessage(availabilityDate) {
    return {
        slug: L10N_PRODUCT_AVAIL_ONE_LEFT
    };
}

/**
 * Get availability message for a sku that has 2 units available
 * @param  {string} availabilityDate      availability date for sku
 * @return {object}        Availability Message
 */
export function getTwoAvailableMessage(availabilityDate) {
    return {
        slug: L10N_PRODUCT_AVAIL_TWO_LEFT
    };
}

/**
 * Get availability message for a sku that is available for drop shipment
 * @param  {string} availabilityDate      availability date for sku
 * @return {object}        Availability Message
 */
export function getDropshipAvailabilityMessage(availabilityDate) {
    return {
        slug: L10N_PRODUCT_AVAIL_DROPSHIP,
        date: availabilityDate,
        params: [availabilityDate]
    };
}

/**
 * Get availability message for a sku that is available for made to order
 * @param  {string} availabilityDate      availability date for sku
 * @return {object}        Availability Message
 */
export function getMadeToOrderAvailabilityMessage(availabilityDate) {
    return {
        slug: L10N_PRODUCT_AVAIL_MADE_TO_ORDER,
        date: availabilityDate,
        params: [availabilityDate]
    };
}

/**
 * Get availability message for a sku that is made to order and in stock
 * @return {object}        Availability Message
 */
export function getMadeToOrderInStockAvailabilityMessage() {
    return {
        slug: L10N_PRODUCT_AVAIL_IN_STOCK_MADE_TO_ORDER
    };
}

/* eslint-disable complexity */
/**
 * Get availability messages for selected sku (re-implementation of ProductModel.get_selected_sku_availability_info)
 * @param {Object} state  state from redux store
 * @param {Object} skuObj current selected sku object
 * @returns {Array|null}
 */
export function getAvailabilityInfo(state, skuObj) {
    if (!skuObj) {
        return null;
    }

    const threshold = state.core.featureConfigs.browseDisplayOptions.lowStockThreshold || 5;
    const availabilityDate = getAvailabilityDate(state, skuObj);
    const isDropShip = skuObj.isDropShip || false;
    const availabilityStatus = _.toString(skuObj.availableStatus);
    const availabilityStatusValueMap = {
        [SKU_AVAILABILITY_AVAILABLE]: 'isAvailable',
        [SKU_AVAILABILITY_OUT_OF_STOCK]: 'isOutOfStock',
        [SKU_AVAILABILITY_DISCONTINUED]: 'isDiscontinued'
    };

    const availability = {
        messages: []
    };

    const isPreOrder = isSelectedSkuPreorder(skuObj, state);
    const isBackOrder = skuObj.backorder > 0;
    const isLowInventory = skuObj.stockLevel > 0 && skuObj.stockLevel <= threshold;
    const isMadeToOrder = isProductMadeToOrder(state);

    const lowInventoryMessage = getLowInventoryAvailabilityMessage(availabilityDate);
    const dropShipMessage = getDropshipAvailabilityMessage(availabilityDate);
    const preOrderMessage = getPreorderAvailabilityMessage(availabilityDate);
    const backOrderMessage = getBackorderAvailabilityMessage(availabilityDate);
    const madeToOrderMessage = getMadeToOrderAvailabilityMessage(availabilityDate);

    const oneAvailableMessage = getOneAvailableMessage(availabilityDate);
    const twoAvailableMessage = getTwoAvailableMessage(availabilityDate);

    if (skuObj.stockLevel === 0) {
        if (skuObj.backorder < threshold) {
            // Made to Order
            if (isBackOrder && isMadeToOrder) {
                availability.isMadeToOrder = true;
                availability.messages.push(madeToOrderMessage);
            } else if (isDropShip) {
                // Dropship with low inventory
                availability.isLowAvailability = true;
                availability.isOutOfStock = false;
                availability.isDropship = true;
                availability.messages.push(lowInventoryMessage);
                availability.messages.push(dropShipMessage);
            } else if (isPreOrder) {
                // Preorder with low inventory
                availability.isLowAvailability = true;
                availability.isOutOfStock = false;
                availability.isPreOrder = true;
                availability.messages.push(lowInventoryMessage);
                availability.messages.push(preOrderMessage);
            } else if (isBackOrder) {
                // Backorder with low inventory
                availability.isLowAvailability = true;
                availability.isOutOfStock = false;
                availability.isBackOrder = true;
                availability.messages.push(lowInventoryMessage);
                availability.messages.push(backOrderMessage);
            } else {
                // Out of stock
                availability.isOutOfStock = true;
                availability.messages.push({ 'slug': L10N_PRODUCT_AVAIL_OUT_OF_STOCK });
            }
        } else if (isPreOrder && isBackOrder && !isDropShip) {
            // Made to Order
            if (isMadeToOrder) {
                availability.isMadeToOrder = true;
                availability.messages.push(madeToOrderMessage);
            } else {
                // Preorder
                availability.isPreOrder = true;
                availability.isOutOfStock = false;
                availability.messages.push(preOrderMessage);
            }
        } else if (isBackOrder) {
            // Made to Order
            if (isMadeToOrder) {
                availability.isMadeToOrder = true;
                availability.messages.push(madeToOrderMessage);
            } else if (isDropShip) {
                availability.isDropship = true;
                availability.isOutOfStock = false;
                availability.messages.push(dropShipMessage);
            } else {
                availability.isBackOrder = true;
                availability.isOutOfStock = false;
                availability.messages.push(backOrderMessage);
            }
        }
    } else if (isLowInventory) {
        // Low Inventory
        availability.isLowAvailability = true;
        availability.isOutOfStock = false;

        if (skuObj.stockLevel === 1) {
            // 1 Unit Available
            availability.messages.push(oneAvailableMessage);
        } else if (skuObj.stockLevel === 2) {
            // 2 Units Available
            availability.messages.push(twoAvailableMessage);
        } else {
            availability.messages.push(lowInventoryMessage);
        }
    } else if (availabilityStatusValueMap[availabilityStatus]) {
        const statusKey = availabilityStatusValueMap[availabilityStatus];

        availability[statusKey] = true;

        switch (availabilityStatus) {
            case SKU_AVAILABILITY_OUT_OF_STOCK:
                availability.messages.push({ 'slug': L10N_PRODUCT_AVAIL_OUT_OF_STOCK });
                break;
            case SKU_AVAILABILITY_DISCONTINUED:
                availability.messages.push({ 'slug': L10N_PRODUCT_AVAIL_DISCONTINUED });
                break;
            case SKU_AVAILABILITY_AVAILABLE:
                availability.isOutOfStock = false;
                break;
            default:
                break;
        }
    }

    return availability;
}
/* eslint-enable complexity */

/**
 * Check if current quantity is valid based on total stock
 * @param {object} skuObj the passed in sku obj for building total stock
 * @param {number} quantity value to check total stock against
 * @returns {true|false}
 */
export function isQuantityValid(skuObj, quantity) {
    const totalStock = getTotalStock(skuObj);

    if (quantity > totalStock) {
        return false;
    }

    return true;
}

/**
 * Check if size type has any available skus in the current
 * color selection
 * @param {object}  secondarySliceItem    hash created in orchestration for sizes
 * @param {string}  colorCode   current selected color code
 * @returns {boolean}
 */
export function isSizeTypeValid(secondarySliceItem, colorCode) {
    if (_.get(secondarySliceItem, 'colorCodesForFit', []).includes(colorCode)) {
        return true;
    }

    return false;
}

/**
 * Get first available size type code
 * color selection
 * @param {array}   sizeSliceItems  slice items for secondary slice
 * @param {string}  colorCode       current selected color code
 * @returns {string|null}
 */
export function getDefaultSizeType(sizeSliceItems, colorCode) {
    // Return type code matchign passed color code
    for (let i = 0; i < sizeSliceItems.length; i++) {
        if (sizeSliceItems[i].colorCodesForFit.includes(colorCode)) {
            return sizeSliceItems[i].code;
        }
    }

    // Otherwie, return the first type code
    return sizeSliceItems[0].code;
}

/**
 * Update product.quickshopUrl
 * @param  {String} oldQuickshopUrl Old Quickshop URL
 * @param  {Object} queryParams     Query Params Hash
 * @return {Strin}                 New Quickshop URL
 */
export function updateQuickshopUrl(oldQuickshopUrl, queryParams) {
    return getQueryParamsUrl(oldQuickshopUrl, queryParams);
}

/**
 * Return the slice item selected based on color code
 * @param {array} sliceItems
 * @param {object} sliceHashToIndex dictionary of code -> slice item index
 * @param {string} colorCode
 * @returns {object}
 */
export function getSelectedSliceItem(sliceItems, sliceHashToIndex, colorCode) {
    const sliceItemIndex = sliceHashToIndex[colorCode];

    if (sliceItemIndex < sliceItems.length && sliceItemIndex >= 0) {
        return sliceItems[sliceItemIndex];
    }

    error(`Slice item not found: ${colorCode}`);
    return null;
}

/**
 * Does the selected slice item have video data?
 * @param  {Object} selectedPrimarySliceItem Selected primary slice item
 * @return {Boolean}            True, if the selected slice item has video; false, otherwise.
 */
export function doesSelectedSliceItemHaveVideo(selectedPrimarySliceItem) {
    return Boolean(selectedPrimarySliceItem && selectedPrimarySliceItem.videos && selectedPrimarySliceItem.videos.length > 0);
}

/**
 * Get selected image ID (re-implementation of ProductModel.get_selected_image_id)
 * @param {object} primarySliceItem
 * @returns {string|null}
 */
export function getSelectedImageId(primarySliceItem = {}) {
    const images = primarySliceItem.images || [];

    if (images && images.length && images[0]) {
        // TODO: UW-137 We are using the first image until default faceout image is implemented
        return images[0];
    }

    return null;
}

/**
 * Gets route info about the page returned by the server
 * @returns {object}
 */
export function getRouteInfo(productSlug) {
    return {
        siteId: Cookies.getCookieByName(COOKIE_NAME_SITE_ID),
        poolId: getInventoryPool(),
        productSlug: productSlug
    };
}

/**
 * Build query object for orchestration call
 * checks for specific query params in the query string
 * and builds them into an object
 *
 * Takes an optional parameter for additional data to include
 * in the form of an object.
 *
 * @returns {object}
 */
function buildQueryObj(existingParams, params = {}) {
    const queryParams = Object.assign({}, existingParams, params);

    return _.pick(queryParams, ALLOWED_PRODUCT_QUERY_PARAMS);
}

export function buildQueryObjForProduct(product, params = {}, forQuickshop = false) {
    const queryParams = _.get(product, 'productMeta.queryParams');
    let existingParams = {};

    if (queryParams) {
        existingParams = queryString.parse(queryParams);
    }

    return buildQueryObj(existingParams, params);
}

export function buildQueryObjFromLocation(params = {}) {
    const existingParams = queryString.parse(window.location.search);

    return buildQueryObj(existingParams, params);
}

export function getUpdatedQueryParams(product, queryParams = {}) {
    const newParams = buildQueryObjForProduct(product, queryParams);

    // Remove the slug query parameter for the PDP
    delete newParams.slug;

    return newParams;
}

/**
 * Updates the URL (without a page reload) based on the selected values
 * @param {object} queryParams Query param hash for product query params
 * @return {string} the new url string
 */
export function updateSelectedQueryParams(product, queryParams = {}) {
    const newParams = getUpdatedQueryParams(product, queryParams);

    const newUrl = getQueryParamsUrl(window.location.href, newParams);

    history.replaceState({}, 'update sku params', newUrl);

    return newUrl;
}

/**
 * Build query object from given object
 * @returns {object}
 */
export function buildQueryStringFromProduct(item) {
    const skuInfo = item.catalogInfo.skuInfo.primarySlice.sliceItems[0].includedSkus[0];
    const qp = {
        color: skuInfo.colorId,
        size: skuInfo.size,
        type: skuInfo.sizeTypeCode
    };

    return queryString.stringify(qp);
}

/**
 * Find the first sku that is flat rate and return
 * its shipRestriction.codeName
 * @returns {string|null}
 */
export function getDefaultFlatRateCode(sliceItems) {
    let i;
    let j;

    for (i = 0; i < sliceItems.length; i++) {
        const sliceItem = sliceItems[i];

        for (j = 0; j < sliceItem.includedSkus.length; j++) {
            const sku = sliceItem.includedSkus[j];

            if (sku.isFlatRate) {
                const tempRestriction = sku.shipRestriction || {};

                return tempRestriction.codeName;
            }
        }
    }

    return null;
}

export function getPrimaryProductRecommendationPlacement(state) {
    if (useGroundhog(state)) {
        return 'item_pagebottom_gh';
    }

    return _.get(state, 'core.constants.thirdParty.default.dynamic_yield.placement_ids.productPrimary', 'pdp');
}

export function isBackInStockEligible(state, selColorCode, selSizeTypeCode, selSizeId, product) {
    const backInStockEnabled = _.get(product, 'backInStockEnabled');
    const sizeInStock = _.get(product, `controlsData.colorData[${selColorCode}].fitData[${selSizeTypeCode}].sizeData[${selSizeId}].inStock`);
    const sizeInColor = _.get(product, `controlsData.colorData[${selColorCode}].fitData[${selSizeTypeCode}].sizeData[${selSizeId}].inColor`);
    const isVintage = _.get(product, `product.isVintage`, false);
    const isMarketPlace = _.get(product, 'product.isMarketPlace', false);

    return backInStockEnabled &&
        isVintage === false &&
        isMarketPlace === false &&
        sizeInStock === false &&
        sizeInColor === true;
}

export function setSkuErrorMessages(payload, fitInStock, sizeInColor) {
    const fitUnavailableInColor = (payload.colorCode || payload.sizeTypeCode) && !fitInStock;
    const sizeUnavailableInColor = payload.colorCode && sizeInColor;
    const noSize = (payload.colorCode || payload.sizeTypeCode) && !sizeInColor;
    const fitAndSizeUnavailable = payload.sizeName && !fitInStock;
    const sizeUnavailableInFit = payload.sizeTypeCode && sizeInColor;
    const sizeUnavailable = payload.sizeName;
    let errorMessage = ERR_SIZE_COLOR_OOS;

    if (fitUnavailableInColor) {
        errorMessage = ERR_SIZE_TYPE_INVALID;
    } else if (sizeUnavailableInColor) {
        errorMessage = ERR_SIZE_NOT_IN_COLOR;
    } else if (noSize) {
        errorMessage = ERR_SELECT_SIZE;
    } else if (fitAndSizeUnavailable) {
        errorMessage = ERR_SIZE_FIT_OOS;
    } else if (sizeUnavailableInFit) {
        errorMessage = ERR_SIZE_NOT_IN_FIT;
    } else if (sizeUnavailable) {
        errorMessage = ERR_SIZE_OOS;
    }

    return errorMessage;
}

export function setColorFitLevelErrorMessages(payload, fitInStock) {
    const fitUnavailableInColor = (payload.colorCode || payload.sizeTypeCode) && !fitInStock;
    let errorMessage = '';

    if (fitUnavailableInColor) {
        errorMessage = ERR_SIZE_TYPE_INVALID;
    }

    return errorMessage;
}

export function getCategorySlugForProduct(product, categoryIdToSlugMapping, validateSlugFunc) {
    let isValid = true;
    let categorySlug = _.get(product, 'browseCategory');

    if (categorySlug && _.isFunction(validateSlugFunc)) {
        isValid = validateSlugFunc(categorySlug);

        if (isValid) {
            return categorySlug;
        }
    }

    if (!isValid || !categorySlug) {
        let parentCategoryId = _.get(product, 'product.parentCategoryId');

        if (!parentCategoryId) {
            parentCategoryId = _.get(product, 'product.parentCategory.id');
        }

        if (parentCategoryId && categoryIdToSlugMapping) {
            categorySlug = _.get(categoryIdToSlugMapping, parentCategoryId);
        }

        if (categorySlug && _.isFunction(validateSlugFunc)) {
            isValid = validateSlugFunc(categorySlug);

            return isValid ? categorySlug : null;
        }
    }

    return categorySlug;
}

export function getBopisStockInfo(state, skuObj) {
    const includePools = _.get(state, 'core.productBopis.defaultStore.storeNumber', {});
    const featureConfigs = _.get(state, 'core.featureConfigs', {});

    // # Establish some baseline variables
    const lowStockThreshold = _.get(featureConfigs, 'browseDisplayOptions.lowStockThreshold', 5);

    // # Pool is the specific store's inventory of the current product
    const current_pool = _.get(skuObj, `pools.${includePools}`, {});

    const stsStockLevel = _.get(current_pool, 'stsStockLevel', 0);
    const ispuStockLevel = _.get(current_pool, 'ispuStockLevel', 0);
    const retailStockLevel = _.get(current_pool, 'retailStockLevel', 0);

    // Quantity is the number of this product the user wants ot buy
    const quantity = _.get(state, 'product.productBopis.quantity', 1);

    // ispu == in store pick up == BOPIS
    const hasEnoughIspuStock = ispuStockLevel >= quantity;

    // Sts == Shop the store
    const hasEnoughStsStock = stsStockLevel >= quantity;

    const hasEnoughRetailStock = retailStockLevel >= quantity;

    const hasLowRetailStock = retailStockLevel <= lowStockThreshold;

    const limitedAvailabilityForInstorePurchase = ispuStockLevel < quantity &&
        stsStockLevel < quantity &&
        retailStockLevel >= quantity &&
        retailStockLevel <= lowStockThreshold;

    // Build the object of orchestrated data to pass back
    const stock = {
        'hasEnoughIspuStock': hasEnoughIspuStock,
        'hasEnoughStsStock': hasEnoughStsStock,
        'hasEnoughRetailStock': hasEnoughRetailStock,
        'hasLowRetailStock': hasLowRetailStock,
        'limitedAvailabilityForInstorePurchase': limitedAvailabilityForInstorePurchase
    };

    return stock;
}

export function isSkuPurchasable(skuAvailableStatus) {
    return PURCHASABLE_SKU_STATUS_CODES.includes(skuAvailableStatus);
}

export function setSalesAttributes(slug, salesAttributes) {
    const eightHoursInSeconds = 28800;
    const associateInformation = {
        'salesAttributes': salesAttributes
    };

    setLocalStorageItemWithFallback(slug, associateInformation, eightHoursInSeconds);
}

export default {
    getTotalStock: getTotalStock,
    getSizeIdFromName: getSizeIdFromName,
    getSkuObj: getSkuObj,
    getSelectedPrice: getSelectedPrice,
    getAvailabilityDate: getAvailabilityDate,
    getAvailabilityInfo: getAvailabilityInfo,
    isQuantityValid: isQuantityValid,
    isSizeTypeValid: isSizeTypeValid,
    getDefaultSizeType: getDefaultSizeType,
    updateSelectedQueryParams: updateSelectedQueryParams,
    updateQuickshopUrl: updateQuickshopUrl,
    getSelectedSliceItem: getSelectedSliceItem,
    getSelectedImageId: getSelectedImageId,
    getUpdatedQueryParams: getUpdatedQueryParams,
    getRouteInfo: getRouteInfo,
    buildQueryObjForProduct: buildQueryObjForProduct,
    buildQueryObjFromLocation: buildQueryObjFromLocation,
    getInventoryPool: getInventoryPool,
    getSelectedColorCode: getSelectedColorCode,
    getProductForAction: getProductForAction,
    getActiveProduct: getActiveProduct,
    getTargetedProduct: getTargetedProduct,
    hasPreorderSkus: hasPreorderSkus,
    actionIsForFamilyProduct: actionIsForFamilyProduct,
    getDefaultFlatRateCode: getDefaultFlatRateCode,
    doesSelectedSliceItemHaveVideo: doesSelectedSliceItemHaveVideo,
    getPrimaryProductRecommendationPlacement: getPrimaryProductRecommendationPlacement,
    isBackInStockEligible: isBackInStockEligible,
    setSkuErrorMessages: setSkuErrorMessages,
    setColorFitLevelErrorMessages: setColorFitLevelErrorMessages,
    getCategorySlugForProduct: getCategorySlugForProduct,
    getBopisStockInfo: getBopisStockInfo,
    isSkuPurchasable: isSkuPurchasable,
    setSalesAttributes: setSalesAttributes
};
